import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as S from './style';

type RulesBlock = {
	items?: string[][];
	lines?: string[];
	type: 'list' | 'paragraph';
};

type RulesArticle = {
	body: {
		blocks: RulesBlock[];
	};
	isBox?: boolean;
	title: string;
};

type RulesChapter = {
	articles: RulesArticle[];
	isNumbered?: boolean;
	title: string;
};

type RulesCategory = {
	id: 'charter' | 'submission';
	label: string;
};

type RulesTranslation = {
	articleTitleFormat: string;
	categories: RulesCategory[];
	charterChapters: RulesChapter[];
	chapterTitleFormat: string;
	submission: {
		items: string[][];
		title: string;
	};
};

const replaceToken = (template: string, token: string, value: number | string) => template.split(token).join(String(value));

const formatLabel = (template: string, values: Record<string, number | string>) =>
	Object.entries(values).reduce((acc, [key, value]) => replaceToken(acc, `{{${key}}}`, value), template);

const renderLines = (lines: string[]) =>
	lines.map((line, index) => (
		<Fragment key={`${line}-${index}`}>
			{index > 0 && <br />}
			{line}
		</Fragment>
	));

const Rules = () => {
	const { t } = useTranslation();
	const rules = t('introduce.rules', { returnObjects: true }) as RulesTranslation;
	const [category, setCategory] = useState<RulesCategory['id']>(rules.categories[0]?.id ?? 'charter');
	const useContinuousArticleNumbering = true;

	let numberedChapterCount = 0;
	let articleCount = 0;

	const numberedChapters = rules.charterChapters.map((chapter) => {
		const isChapterNumbered = chapter.isNumbered !== false;
		const chapterNumber = isChapterNumbered ? (numberedChapterCount += 1) : null;
		const articleNumberBase = useContinuousArticleNumbering && isChapterNumbered ? articleCount : 0;

		const articles = chapter.articles.map((article, articleIndex) => ({
			...article,
			number: articleNumberBase + articleIndex + 1,
		}));

		if (useContinuousArticleNumbering && isChapterNumbered) {
			articleCount += chapter.articles.length;
		}

		return {
			...chapter,
			articles,
			chapterNumber,
		};
	});

	return (
		<S.RulesWrapper>
			<S.RulesCategoryList>
				{rules.categories.map((item) => (
					<S.RulesCategory key={item.id} className={item.id === category ? 'active' : ''} onClick={() => setCategory(item.id)}>
						{item.label}
					</S.RulesCategory>
				))}
			</S.RulesCategoryList>

			{category === 'charter' && (
				<S.RulesContainer>
					{numberedChapters.map((chapter, chapterIndex) => (
						<S.RulesContent key={`${chapter.title}-${chapterIndex}`}>
							<S.RulesTitle>
								{chapter.chapterNumber ? formatLabel(rules.chapterTitleFormat, { number: chapter.chapterNumber, title: chapter.title }) : chapter.title}
							</S.RulesTitle>
							{chapter.articles.map((article) => {
								const Wrapper = article.isBox ? S.RulesContentItemBox : S.RulesContentItem;

								return (
									<Wrapper key={`${chapter.title}-${article.title}-${article.number}`}>
										<S.RulesSubTitle>{formatLabel(rules.articleTitleFormat, { number: article.number, title: article.title })}</S.RulesSubTitle>
										{article.body.blocks.map((block, blockIndex) =>
											block.type === 'paragraph' ? (
												<S.RulesDescription key={`${article.title}-paragraph-${blockIndex}`}>{renderLines(block.lines ?? [])}</S.RulesDescription>
											) : (
												<S.DescriptionBox key={`${article.title}-list-${blockIndex}`}>
													{(block.items ?? []).map((item, itemIndex) => (
														<li key={`${article.title}-list-item-${itemIndex}`}>{renderLines(item)}</li>
													))}
												</S.DescriptionBox>
											)
										)}
									</Wrapper>
								);
							})}
						</S.RulesContent>
					))}
				</S.RulesContainer>
			)}

			{category === 'submission' && (
				<S.RulesContainer>
					<S.RulesSubmissionContainer>
						<S.RulesSubTitle>{rules.submission.title}</S.RulesSubTitle>
						<S.RulesSubmissionDescriptionBox>
							{rules.submission.items.map((item, index) => (
								<li key={`submission-${index}`}>{renderLines(item)}</li>
							))}
						</S.RulesSubmissionDescriptionBox>
					</S.RulesSubmissionContainer>
				</S.RulesContainer>
			)}
		</S.RulesWrapper>
	);
};

export default Rules;
